import {
  IntegrationActivityEventStatus,
  IntegrationActivityEventType,
  type IntegrationActivityEvent,
} from 'src/api/generated';
import type { EventTimelineItem } from 'src/modules/shared/components/molecules/event-timeline';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import type { IntegrationActivityEventDetailKey } from 'src/modules/shared/types/api/integration-activity-event.type';
import { isRecord } from '../types.util';

export const getIntegrationActivityEventLabelKey = (
  type: IntegrationActivityEvent['type']
): TranslationKey =>
  `integrations.activity.type.${String(type)}` as TranslationKey;

export const resolveIntegrationActivityEventTone = (
  event: IntegrationActivityEvent
): EventTimelineItem['tone'] => {
  if (
    event.type === IntegrationActivityEventType.PUBLICATION_PUBLISH_ENQUEUED ||
    event.type === IntegrationActivityEventType.PUBLICATION_PAUSE_ENQUEUED ||
    event.type === IntegrationActivityEventType.PUBLICATION_UNPUBLISH_ENQUEUED
  ) {
    return 'warning';
  }

  switch (event.status) {
    case IntegrationActivityEventStatus.SUCCESS:
      return 'success';
    case IntegrationActivityEventStatus.WARNING:
      return 'warning';
    case IntegrationActivityEventStatus.ERROR:
      return 'danger';
    case IntegrationActivityEventStatus.INFO:
    default:
      return 'default';
  }
};

export const isSyncActivityEvent = (event: IntegrationActivityEvent): boolean =>
  event.type === IntegrationActivityEventType.SYNC_APPLIED ||
  event.type === IntegrationActivityEventType.SYNC_SKIPPED ||
  event.type === IntegrationActivityEventType.SYNC_FAILED ||
  event.type === IntegrationActivityEventType.SYNC_RECONCILIATION_APPLIED;

const humanizeActivityValue = (value: string): string =>
  value
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part, index) =>
      index === 0 ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part
    )
    .join(' ');

const resolvePortalTargetValue = (
  value: string,
  t: I18nContextValue['t']
): string =>
  t(
    value === 'meli_poi'
      ? 'catalog.properties.editor.publishing.target.meliPoi'
      : 'catalog.properties.editor.publishing.target.meli'
  );

const resolveAssigneeSourceValue = (
  value: string,
  t: I18nContextValue['t']
): string =>
  t(
    value === 'email'
      ? 'catalog.properties.meli.currentPublication.history.value.assigneeSource.email'
      : value === 'phone'
        ? 'catalog.properties.meli.currentPublication.history.value.assigneeSource.phone'
        : 'catalog.properties.meli.currentPublication.history.value.assigneeSource.none'
  );

const resolveAssigneeResolutionValue = (
  value: string,
  t: I18nContextValue['t']
): string =>
  t(
    value === 'resolved'
      ? 'catalog.properties.meli.currentPublication.history.value.assigneeResolution.resolved'
      : value === 'ambiguous'
        ? 'catalog.properties.meli.currentPublication.history.value.assigneeResolution.ambiguous'
        : 'catalog.properties.meli.currentPublication.history.value.assigneeResolution.notFound'
  );

const resolvePropertyActionValue = (
  value: string,
  t: I18nContextValue['t']
): string =>
  t(
    value === 'created'
      ? 'catalog.properties.meli.currentPublication.history.value.propertyAction.created'
      : 'catalog.properties.meli.currentPublication.history.value.propertyAction.updated'
  );

const resolvePublicationActionValue = (
  value: string,
  t: I18nContextValue['t']
): string =>
  t(
    value === 'created'
      ? 'catalog.properties.meli.currentPublication.history.value.publicationAction.created'
      : 'catalog.properties.meli.currentPublication.history.value.publicationAction.updated'
  );

const resolveSourceEventTypeValue = (
  value: string,
  t: I18nContextValue['t']
): string =>
  t(
    value === 'notification'
      ? 'integrations.activity.value.sourceEventType.notification'
      : 'integrations.activity.value.sourceEventType.backfill'
  );

const resolveStringActivityValue = (params: {
  key: string;
  t: I18nContextValue['t'];
  value: string;
}): string => {
  const { key, t, value } = params;

  switch (key) {
    case 'portalTarget':
      return resolvePortalTargetValue(value, t);
    case 'assigneeSource':
      return resolveAssigneeSourceValue(value, t);
    case 'assigneeResolution':
      return resolveAssigneeResolutionValue(value, t);
    case 'propertyAction':
      return resolvePropertyActionValue(value, t);
    case 'publicationAction':
      return resolvePublicationActionValue(value, t);
    case 'sourceEventType':
      return resolveSourceEventTypeValue(value, t);
    case 'staleReason':
      return t(
        `integrations.activity.value.staleReason.${value}` as TranslationKey
      );
    case 'status':
    case 'previousStatus':
    case 'healthStatus':
    case 'previousHealthStatus':
    case 'reason':
    case 'remoteStatus':
      return humanizeActivityValue(value);
    default:
      return value;
  }
};

const resolveActivityValue = (params: {
  key: string;
  t: I18nContextValue['t'];
  value: string | number | boolean;
}): string => {
  const { value } = params;

  if (typeof value === 'boolean') {
    return value
      ? params.t(
          'catalog.properties.meli.currentPublication.history.value.paymentRequired.yes'
        )
      : params.t(
          'catalog.properties.meli.currentPublication.history.value.paymentRequired.no'
        );
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return resolveStringActivityValue({
    key: params.key,
    t: params.t,
    value,
  });
};

const getEventDetailValue = (
  event: IntegrationActivityEvent,
  key: IntegrationActivityEventDetailKey
): string | number | boolean | null => {
  const details = isRecord(event.details) ? event.details : null;
  const directValue = details?.[key];

  if (
    typeof directValue === 'string' ||
    typeof directValue === 'number' ||
    typeof directValue === 'boolean'
  ) {
    return directValue;
  }

  if (key === 'processedCount') {
    const legacyValue = details?.processedItemCount;
    return typeof legacyValue === 'number' ? legacyValue : null;
  }

  if (key === 'failedCount') {
    const legacyValue = details?.failedItemCount;
    return typeof legacyValue === 'number' ? legacyValue : null;
  }

  return null;
};

const detailLabelKeyByKey: Record<
  IntegrationActivityEventDetailKey,
  TranslationKey
> = {
  itemId: 'catalog.properties.meli.currentPublication.history.details.itemId',
  portalTarget:
    'catalog.properties.meli.currentPublication.history.details.portalTarget',
  listingTypeId:
    'catalog.properties.meli.currentPublication.history.details.listingTypeId',
  categoryId:
    'catalog.properties.meli.currentPublication.history.details.categoryId',
  remoteStatus:
    'catalog.properties.meli.currentPublication.history.details.remoteStatus',
  reason: 'catalog.properties.meli.currentPublication.history.details.reason',
  propertyAction:
    'catalog.properties.meli.currentPublication.history.details.propertyAction',
  publicationAction:
    'catalog.properties.meli.currentPublication.history.details.publicationAction',
  assigneeSource:
    'catalog.properties.meli.currentPublication.history.details.assigneeSource',
  assigneeResolution:
    'catalog.properties.meli.currentPublication.history.details.assigneeResolution',
  providerSubject: 'integrations.activity.details.providerSubject',
  processedCount: 'integrations.activity.details.processedCount',
  failedCount: 'integrations.activity.details.failedCount',
  limit: 'integrations.activity.details.limit',
  status: 'catalog.properties.meli.currentPublication.history.details.status',
  previousStatus: 'integrations.activity.details.previousStatus',
  healthStatus: 'integrations.activity.details.healthStatus',
  previousHealthStatus: 'integrations.activity.details.previousHealthStatus',
  sourceEventType: 'integrations.activity.details.sourceEventType',
  staleReason: 'integrations.activity.details.staleReason',
};

const detailKeysByEventType: Partial<
  Record<IntegrationActivityEvent['type'], IntegrationActivityEventDetailKey[]>
> = {
  connection_connected: ['status', 'healthStatus'],
  connection_updated: [
    'status',
    'healthStatus',
    'previousStatus',
    'previousHealthStatus',
  ],
  sync_requested: ['providerSubject', 'limit'],
  sync_started: ['providerSubject', 'limit'],
  sync_completed: ['processedCount', 'failedCount'],
  sync_superseded: ['providerSubject', 'staleReason'],
  sync_applied: [
    'itemId',
    'propertyAction',
    'publicationAction',
    'assigneeSource',
    'assigneeResolution',
    'sourceEventType',
  ],
  sync_skipped: ['itemId', 'reason', 'sourceEventType'],
  sync_failed: ['itemId', 'reason', 'sourceEventType'],
  sync_reconciliation_applied: ['processedCount', 'failedCount'],
  publication_created: ['itemId', 'portalTarget'],
  publication_draft_prepared: ['itemId', 'listingTypeId', 'categoryId'],
  publication_publish_enqueued: ['itemId', 'portalTarget'],
  publication_published: ['itemId', 'portalTarget', 'remoteStatus'],
  publication_publish_failed: ['itemId', 'reason'],
  publication_pause_enqueued: ['itemId'],
  publication_paused: ['itemId', 'remoteStatus'],
  publication_pause_failed: ['itemId', 'reason'],
  publication_unpublish_enqueued: ['itemId'],
  publication_unpublished: ['itemId', 'remoteStatus'],
  publication_unpublish_failed: ['itemId', 'reason'],
};

export const buildIntegrationActivityEventTags = (
  event: IntegrationActivityEvent,
  t: I18nContextValue['t']
): EventTimelineItem['tags'] => {
  if (!isRecord(event.details)) {
    return undefined;
  }

  const keys = detailKeysByEventType[event.type] ?? [];
  const tags = keys
    .map((key) => {
      const value = getEventDetailValue(event, key);

      if (value === null) {
        return null;
      }

      return {
        key,
        label: t(detailLabelKeyByKey[key]),
        value: resolveActivityValue({ key, value, t }),
      };
    })
    .filter((tag): tag is NonNullable<typeof tag> => tag != null);

  return tags.length > 0 ? tags : undefined;
};
